import { createContext, useContext, useEffect, useState } from "react";
import { api, isNetworkError } from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function withRetry(fn, tries = 3) {
  for (let i = 0; ; i += 1) {
    try {
      return await fn();
    } catch (e) {
      if (!isNetworkError(e) || i >= tries - 1) throw e;
      await sleep(800 * (i + 1));
    }
  }
}

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    withRetry(() => api.get("/auth/me")).then((r) => setUser(r.data)).catch(() => setUser(false));
    const id = api.interceptors.response.use(undefined, (err) => {
      const url = err?.config?.url || "";
      if (err?.response?.status === 401 && !url.includes("/auth/")) setUser(false);
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (username, password) => {
    const { data } = await withRetry(() => api.post("/auth/login", { username, password }), 2);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, login, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
